import { ResponsiveUtils, ScrollUtils } from '@olbbank/vue-components';
import Vue, { VNode } from 'vue';

const HEADER_SCROLL_CLASS = 'l-head--scrolled';
const QUICKNAV_SCROLL_CLASS = 'wuw-scrolled';

/**
 * Simple utility function to toggle a class name on an element based on a boolean variable.
 * Vanilla JS for jQuery's `toggleClass()` method.
 * @param element The HTML Element, on which the given class name should be toggled
 * @param className The class name to be toggled
 * @param add If true, the class name is added to the element's classes, if false it is removed
 */
function toggleClass(element: HTMLElement, className: string, add: boolean): void {
	if (!element || !className) {
		return;
	}
	let classString = element.className;
	const nameIndex = classString.indexOf(className);
	// if the given class name should be added and is not yet present
	if (add && nameIndex < 0) {
		classString += classString.length ? ' ' + className : className;
	}
	// other case: given class name is present but should be removed
	else if (!add && nameIndex > 0) {
		// case 1: more than 1 class name
		classString = classString.replace(' ' + className, '');
		// case 2: only the given class name
		classString = classString.replace(className, '');
	}
	element.className = classString;
}

type WUWVueContext = {
	scrollUtils: ScrollUtils;
	responsiveUtils: ResponsiveUtils;
} & Vue;

/**
 * Directive to handle scrolling on a W&W website:
 * On mobile screens, the header wraps to two lines (logo on the first, navigation items on the second line).
 * When the user scrolls down, the logo slides up, so that there's only one line left and the header is a bit thinner.
 * This directive takes care, that the appropriate classes for hiding the logo and adjusting all elements depending on the header height are set.
 */
export default Vue.directive('wuw-header', {
	inserted: function(el: HTMLElement, _, vnode: VNode) {
		const vueContext = vnode.context as WUWVueContext;
		const responsiveUtils = vueContext?.responsiveUtils as ResponsiveUtils;
		let scrollUtils = null as null | ScrollUtils;
		let quicknavOverlays = null as null | NodeList;
		document.addEventListener('scroll', () => {
			if (!quicknavOverlays) {
				// gather all quick nav overlays
				// we have to do so via query selector since these are portals and can thus (unfortunately) not be retrieved via ref
				quicknavOverlays = document.querySelectorAll('.olb-quick-nav-overlay .olb-overlay-pane');
			}
			if (!scrollUtils) {
				// the scroll utils are registered on the Vue instance
				scrollUtils = vueContext.scrollUtils as ScrollUtils;
				return;
			}
			// only make the header thinner for mobile
			if (responsiveUtils.isSm()) {
				// toggle the scrolled class both on the navigation and on the quick nav
				quicknavOverlays &&
					quicknavOverlays.forEach((quicknav) =>
						toggleClass(
							quicknav as HTMLElement,
							QUICKNAV_SCROLL_CLASS,
							// why ever: TypeScript throws an error here and says, `scrollUtils` is possibly undefined.
							// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
							// @ts-ignore
							window.scrollY > scrollUtils?.getHeaderOffset(),
						),
					);
				toggleClass(el, HEADER_SCROLL_CLASS, window.scrollY > scrollUtils.getHeaderOffset());
			}
		});

		// when widening the window, we have to remove the scrolled classes, otherwise the (quick) navigation style would break
		window.addEventListener('resize', () => {
			if (responsiveUtils.isMdUp()) {
				quicknavOverlays &&
					quicknavOverlays.forEach((quicknav) =>
						toggleClass(quicknav as HTMLElement, QUICKNAV_SCROLL_CLASS, false),
					);
				toggleClass(el, HEADER_SCROLL_CLASS, false);
			}
		});
	},
});
